/*
  Used to create a quick view modal for product details.

  Used by:
  - /frontend/js/extra/load-libraries.js if has a element with data-quick-view-modal attribute
  - /snippets/quick-view-modal-base.liquid
*/
import { createFocusTrap } from "focus-trap";
import { add, listen, qs, qsa, remove } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { makeRequest } from "@/js/lib/xhr";
import dispatchCustomEvent from "@/js/lib/dispatch-custom-event";

import { emit, on } from "@/js/glow/events";
import { default as Product } from "@/js/lib/product/product";
import { animateQuickView, shouldAnimate } from "@/js/lib/animation";

const classes = {
  visible: "is-visible",
  active: "active",
  fixed: "is-fixed",
};

const selectors = {
  closeBtn: "[data-modal-close]",
  wash: ".modal__wash",
  modalContent: ".quick-view-modal__content",
  loadingMessage: ".quick-view-modal-loading-indicator",
};

const quickViewModal = node => {
  const focusTrap = createFocusTrap(node, { allowOutsideClick: true, returnFocusOnDeactivate: false });
  const wash = qs(selectors.wash, node);
  const closeButton = qs(selectors.closeBtn, node);
  const modalContent = qs(selectors.modalContent, node);
  const loadingMessage = qs(selectors.loadingMessage, node);
  let quickViewAnimation = null;
  if (shouldAnimate(node)) {
    quickViewAnimation = animateQuickView(node);
  }
  let product;

  const events = [
    listen([wash, closeButton], "click", e => {
      e.preventDefault();
      _close();
    }),
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) _close();
    }),
    on("quick-view:open", (state, { productUrl }) => {
      _renderProductContent(productUrl);
      _open();
    }),
    on("quick-view:close", () => {
      _close();
    }),
  ];

  const _renderProductContent = productUrl => {
    const xhrUrl = `${productUrl}${
      productUrl.includes("?") ? "&" : "?"
    }view=quick-view`;

    makeRequest("GET", xhrUrl).then(response => {
      // Remove existing scripts in the body (if already added)
      if (window.Recharge) {
        window.Recharge = undefined;
        window.initRecharge = undefined;
        window.onRechargeLoad = undefined;
      }

      let container = document.createElement("div");
      container.innerHTML = response;
      const productElement = qs("[data-is-quick-view]", container);
      remove(modalContent, "empty");
      modalContent.innerHTML = "";

      if (productElement && productElement.querySelector('.container-recharge')) {
        let variantId = productElement.dataset.variantId;

        // Select all elements with name="id" and value equal to variantId
        document.querySelectorAll(`[name="id"][value="${variantId}"], [name="id"] [value="${variantId}"]`).forEach(element => {
          // Store the original value in a dataset attribute
          element.dataset.originalValue = element.value;
          // Remove the value and store it in a dataset attribute
          element.dataset.variantId = variantId;
          element.value = '';
        });
      }

      modalContent.appendChild(productElement);
      const renderedProductElement = qs("[data-is-quick-view]", modalContent);

      if (shouldAnimate(node)) {
        quickViewAnimation.animate();
      }

      product = new Product(renderedProductElement);

      // Remove existing scripts in the body (if already added)
      document.body.querySelectorAll("script[data-quickview-script]").forEach(script => script.remove());

      // Execute scripts within the loaded content
      productElement.querySelectorAll('script, link[rel="modulepreload"]').forEach(script => {
        if (script.tagName === "LINK" && script.rel === "modulepreload") {
          const existingLink = document.querySelector(`link[rel="modulepreload"][href="${script.href}"]`);
          if (!existingLink) {
            const newLink = document.createElement("link");
            newLink.rel = "modulepreload";
            newLink.href = script.href;
            newLink.crossOrigin = script.crossOrigin || "anonymous";
            document.head.appendChild(newLink);
          }
          return; // Skip modulepreload links
        }
        
        // Create a new script element
        const newScript = document.createElement("script");
        // Mark the script for identification
        newScript.setAttribute("data-quickview-script", "true");
        if (script.src && script.type !== "application/json") {
          // If the script has a src, load it dynamically
          const uniqueSrc = `${script.src}?_=${Date.now()}`;
          newScript.src = uniqueSrc;
          newScript.type = script.type || "text/javascript";
          newScript.async = false;
        } else {
          // If the script is inline, copy its content
          newScript.textContent = script.textContent;
        }
        document.body.appendChild(newScript); // Add the script to the DOM to execute it
      });
      
      dispatchCustomEvent("quickview:loaded");

      document.dispatchEvent(new CustomEvent('add-to-cart:enable'));

      window.lion.ui.refresh();
    });
  };

  const _open = () => {
    add(node, classes.fixed);

    setTimeout(() => {
      add(node, classes.active);
      setTimeout(() => {
        add(node, classes.visible);
        focusTrap.activate();
      }, 50);
    }, 50);

    document.body.setAttribute("data-fluorescent-overlay-open", "true");

    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  };

  const _close = () => {
    focusTrap.deactivate();
    remove(node, classes.visible);
    remove(node, classes.active);
    document.body.setAttribute("data-fluorescent-overlay-open", "false");
    enableBodyScroll(node);
    emit("quick-cart:scrollup");

    setTimeout(() => {
      remove(node, classes.fixed);
      if (shouldAnimate(node)) {
        quickViewAnimation.reset();
      }
      modalContent.innerHTML = "";
      modalContent.appendChild(loadingMessage);
      add(modalContent, "empty");
      product?.unload();

      // Restore original values for elements with name="id"
      const elements = document.querySelectorAll('[name="id"][data-original-value]');
      elements.forEach(element => {
        element.value = element.dataset.originalValue; // Restore the original value
        delete element.dataset.originalValue; // Clean up the dataset attribute
      });
    }, 500);
  };

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
};

export default quickViewModal;
